import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, MapPinIcon, CurrencyDollarIcon, UserGroupIcon, EyeIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/outline';
import PlotSearchModal from './PlotSearchModal';
import FlexFieldValues from './FlexFieldValues';
import DocumentGenerationPanel from './DocumentGenerationPanel';

// LocalStorage keys for sort persistence
const CONTACTS_SORT_COLUMN_KEY = 'contactsSortColumn';
const CONTACTS_SORT_ORDER_KEY = 'contactsSortOrder';

const Contacts = ({ previewMode = false }) => {
  const { token } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mailingAddress: '',
    emailAddress: '',
    phoneNumber: '',
    isVeteran: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasHandledUrlParams = useRef(false);

  // Pagination and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [pageSize] = useState(10);
  const searchDebounceRef = useRef(null);

  // Filter state
  const [filterHasPlots, setFilterHasPlots] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Sort state with localStorage persistence
  const [sortColumn, setSortColumn] = useState(() => {
    return localStorage.getItem(CONTACTS_SORT_COLUMN_KEY) || 'lastName';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem(CONTACTS_SORT_ORDER_KEY) || 'ASC';
  });

  // Flex field states for create/edit form
  const [flexDefs, setFlexDefs] = useState([]);
  const [flexVals, setFlexVals] = useState({});

  // Contact detail view states
  const [viewingContact, setViewingContact] = useState(null);
  const [contactPlots, setContactPlots] = useState([]);
  const [contactSales, setContactSales] = useState([]);
  const [associatedContacts, setAssociatedContacts] = useState([]);
  const [isPlotModalOpen, setIsPlotModalOpen] = useState(false);
  const [relationshipType, setRelationshipType] = useState('owner');

  // Fetch contacts with pagination, search, filters, and sorting
  const fetchContacts = useCallback(async (page = 1, search = '', plotsFilter = '', sort = { column: 'lastName', order: 'ASC' }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        search: search,
        sortBy: sort.column,
        sortOrder: sort.order
      });
      if (plotsFilter) params.append('hasPlots', plotsFilter);
      const response = await fetch(`/api/contacts?${params}`, {
        headers: { Authorization: token }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
        setTotalPages(data.pagination.totalPages);
        setTotalContacts(data.pagination.total);
        setCurrentPage(data.pagination.page);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [token, pageSize]);

  // Fetch contacts when page, filters, or sort changes
  useEffect(() => {
    fetchContacts(currentPage, searchTerm, filterHasPlots, { column: sortColumn, order: sortOrder });
  }, [currentPage, filterHasPlots, sortColumn, sortOrder, fetchContacts]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchContacts(1, searchTerm, filterHasPlots, { column: sortColumn, order: sortOrder });
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm, sortColumn, sortOrder, fetchContacts]);

  // Handle URL params to auto-open modals
  useEffect(() => {
    if (hasHandledUrlParams.current || contacts.length === 0) return;

    const action = searchParams.get('action');
    const id = searchParams.get('id');

    if (action === 'new') {
      hasHandledUrlParams.current = true;
      handleOpenForm();
      setSearchParams({});
    } else if (action === 'edit' && id) {
      const contact = contacts.find(c => c.id === parseInt(id));
      if (contact) {
        hasHandledUrlParams.current = true;
        handleOpenForm(contact);
        setSearchParams({});
      }
    }
  }, [contacts, searchParams]);

  // Refresh data helper
  const refreshContacts = () => {
    fetchContacts(currentPage, searchTerm, filterHasPlots, { column: sortColumn, order: sortOrder });
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    let newOrder = 'ASC';
    if (sortColumn === column) {
      // Toggle order if clicking the same column
      newOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    setSortColumn(column);
    setSortOrder(newOrder);
    localStorage.setItem(CONTACTS_SORT_COLUMN_KEY, column);
    localStorage.setItem(CONTACTS_SORT_ORDER_KEY, newOrder);
    setCurrentPage(1);
  };

  // Render sort icon for column headers
  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortOrder === 'ASC' ? (
      <ChevronUpIcon className="h-4 w-4 ml-1 inline-block" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 ml-1 inline-block" />
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterHasPlots('');
    setCurrentPage(1);
  };

  const fetchFlexDefs = async (entityType, entityId) => {
    try {
      const response = await fetch(`/api/flex-fields/definitions?entityType=${entityType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setFlexDefs(await response.json());
      }
    } catch (err) { console.error('Error fetching flex defs:', err); }

    if (entityId) {
      try {
        const response = await fetch(`/api/flex-fields/values/${entityType}/${entityId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const valMap = {};
          data.forEach(v => { valMap[v.definitionId] = v.value; });
          setFlexVals(valMap);
        }
      } catch (err) { console.error('Error fetching flex vals:', err); }
    } else {
      setFlexVals({});
    }
  };

  const handleOpenForm = (contact = null) => {
    if (contact) {
      setSelectedContact(contact);
      setFormData({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        mailingAddress: contact.mailingAddress || '',
        emailAddress: contact.emailAddress || '',
        phoneNumber: contact.phoneNumber || '',
        isVeteran: contact.isVeteran || false
      });
      fetchFlexDefs('contact', contact.id);
    } else {
      setSelectedContact(null);
      setFormData({
        firstName: '',
        lastName: '',
        mailingAddress: '',
        emailAddress: '',
        phoneNumber: '',
        isVeteran: false
      });
      fetchFlexDefs('contact', null);
    }
    setIsFormOpen(true);
    setError('');
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedContact(null);
    setError('');
  };

  const saveFlexFieldValues = async (entityId) => {
    const hasValues = flexDefs.some(def => flexVals[def.id]);
    if (!hasValues) return;

    const valueArray = flexDefs.map(def => ({
      definitionId: def.id,
      value: flexVals[def.id] || ''
    }));

    try {
      await fetch(`/api/flex-fields/values/contact/${entityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ values: valueArray })
      });
    } catch (err) { console.error('Error saving flex values:', err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = selectedContact ? `/api/contacts/${selectedContact.id}` : '/api/contacts';
      const method = selectedContact ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Failed to save contact');
      }

      const savedContact = await response.json();

      // Save flex field values
      const entityId = selectedContact ? selectedContact.id : savedContact.id;
      await saveFlexFieldValues(entityId);

      refreshContacts();
      handleCloseForm();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      const response = await fetch(`/api/contacts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });

      if (response.ok) {
        refreshContacts();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete contact');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete contact');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleViewContact = async (contact) => {
    setViewingContact(contact);
    setContactPlots([]);
    setContactSales([]);
    setAssociatedContacts([]);

    try {
      // Fetch plots and sales for this contact in parallel
      const [plotsRes, salesRes] = await Promise.all([
        fetch(`/api/plot-contacts/contact/${contact.id}`, {
          headers: { Authorization: token }
        }),
        fetch('/api/sales?all=true', {
          headers: { Authorization: token }
        })
      ]);

      // Handle sales - filter for this contact
      if (salesRes.ok) {
        const allSales = await salesRes.json();
        const filteredSales = allSales.filter(s => s.contactId === contact.id);
        setContactSales(filteredSales);
      }

      if (plotsRes.ok) {
        const plots = await plotsRes.json();
        setContactPlots(plots);

        // Find associated contacts (other contacts on the same plots)
        if (plots.length > 0) {
          const plotIds = plots.map(p => p.plotId);
          const associatedContactsMap = new Map();

          // Fetch contacts for each plot
          for (const plotId of plotIds) {
            const plotContactsRes = await fetch(`/api/plot-contacts/plot/${plotId}`, {
              headers: { Authorization: token }
            });
            if (plotContactsRes.ok) {
              const plotContacts = await plotContactsRes.json();
              plotContacts.forEach(pc => {
                if (pc.contactId !== contact.id && pc.Contact) {
                  const existing = associatedContactsMap.get(pc.contactId);
                  if (existing) {
                    // Add this plot to their shared plots
                    existing.sharedPlots.push({
                      plotId: plotId,
                      plotAlias: pc.PlotPoly?.alias || `Plot ${plotId}`,
                      relationship: pc.relationshipType
                    });
                  } else {
                    associatedContactsMap.set(pc.contactId, {
                      contact: pc.Contact,
                      sharedPlots: [{
                        plotId: plotId,
                        plotAlias: pc.PlotPoly?.alias || `Plot ${plotId}`,
                        relationship: pc.relationshipType
                      }]
                    });
                  }
                }
              });
            }
          }

          setAssociatedContacts(Array.from(associatedContactsMap.values()));
        }
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }
  };

  const handleAddPlotToContact = async (plot) => {
    if (!viewingContact) return;

    try {
      const response = await fetch('/api/plot-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          plotId: plot.fid,
          contactId: viewingContact.id,
          relationshipType: relationshipType
        })
      });

      if (response.ok) {
        const newPlotContact = await response.json();
        // Enhance with plot data for display
        newPlotContact.PlotPoly = plot;
        setContactPlots(prev => [...prev, newPlotContact]);
        setRelationshipType('owner');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to add plot to contact');
      }
    } catch (error) {
      console.error('Error adding plot to contact:', error);
      alert('Failed to add plot to contact');
    }
  };

  const handleRemovePlotFromContact = async (plotContactId) => {
    try {
      const response = await fetch(`/api/plot-contacts/${plotContactId}`, {
        method: 'DELETE',
        headers: { Authorization: token }
      });

      if (response.ok) {
        setContactPlots(prev => prev.filter(pc => pc.id !== plotContactId));
      } else {
        alert('Failed to remove plot from contact');
      }
    } catch (error) {
      console.error('Error removing plot from contact:', error);
    }
  };

  const calculateTotal = (items) => {
    return items?.reduce((sum, item) => sum + (item.price * item.qty), 0) || 0;
  };

  const handleViewPlotOnMap = (plotId) => {
    navigate(`/plots?highlight=${plotId}`);
  };

  const handleCreateSaleForContact = (contact) => {
    navigate(`/sales?action=new&contactId=${contact.id}`);
  };

  const handleEditFromView = () => {
    handleOpenForm(viewingContact);
    setViewingContact(null);
  };

  // Preview mode shows a simplified view
  if (previewMode) {
    return (
      <div className="overflow-auto w-full rounded-lg bg-white p-4 shadow ring-1 ring-gray-200">
        <h3 className="text-sm font-medium text-gray-500 mb-3">Recent Contacts</h3>
        <ul className="divide-y divide-gray-100">
          {contacts.slice(0, 5).map((contact) => (
            <li key={contact.id} className="py-2">
              <p className="text-sm font-medium text-gray-900">
                {contact.firstName} {contact.lastName}
              </p>
              <p className="text-xs text-gray-500">{contact.emailAddress}</p>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage contact information for plot owners, family members, and other related parties.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => handleOpenForm()}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Search Bar and Filters */}
      <div className="mt-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              placeholder="Search contacts by name, email, phone, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ${
              showFilters || filterHasPlots
                ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5 mr-1" aria-hidden="true" />
            Filters
            {filterHasPlots && (
              <span className="ml-1.5 rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs text-white">
                1
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 ring-1 ring-gray-200">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="min-w-[200px]">
                <label htmlFor="filterPlots" className="block text-sm font-medium text-gray-700 mb-1">
                  Plot Association
                </label>
                <select
                  id="filterPlots"
                  value={filterHasPlots}
                  onChange={(e) => { setFilterHasPlots(e.target.value); setCurrentPage(1); }}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                >
                  <option value="">All Contacts</option>
                  <option value="true">Has Plot Associations</option>
                  <option value="false">No Plot Associations</option>
                </select>
              </div>
              {filterHasPlots && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}

        {totalContacts > 0 && (
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalContacts)} of {totalContacts} contacts
          </p>
        )}
      </div>

      {/* Contacts Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {contacts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow ring-1 ring-gray-200">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No contacts</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating a new contact.</p>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => handleOpenForm()}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Add Contact
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('lastName')}
                      >
                        Name
                        {renderSortIcon('lastName')}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('emailAddress')}
                      >
                        Email
                        {renderSortIcon('emailAddress')}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Phone
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {contacts.map((contact) => (
                      <tr
                        key={contact.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewContact(contact)}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">
                            {contact.firstName} {contact.lastName}
                          </div>
                          <div className="text-gray-500">{contact.mailingAddress}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {contact.emailAddress || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {contact.phoneNumber || '-'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleViewContact(contact)}
                              className="text-gray-600 hover:text-gray-900"
                              title="View"
                            >
                              <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenForm(contact)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(contact.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalContacts)}</span> of{' '}
                <span className="font-medium">{totalContacts}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pageNum === currentPage
                            ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return (
                      <span
                        key={pageNum}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseForm} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {selectedContact ? 'Edit Contact' : 'Add New Contact'}
                </h3>
                <form onSubmit={handleSave} className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="mailingAddress" className="block text-sm font-medium text-gray-700">
                      Mailing Address
                    </label>
                    <input
                      type="text"
                      name="mailingAddress"
                      id="mailingAddress"
                      value={formData.mailingAddress}
                      onChange={(e) => setFormData({ ...formData, mailingAddress: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="emailAddress"
                        id="emailAddress"
                        value={formData.emailAddress}
                        onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        id="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isVeteran"
                      name="isVeteran"
                      checked={formData.isVeteran}
                      onChange={(e) => setFormData({ ...formData, isVeteran: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="isVeteran" className="ml-2 block text-sm font-medium text-gray-700">
                      Veteran
                    </label>
                  </div>

                  {flexDefs.length > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Additional Fields</h4>
                      {flexDefs.map(def => (
                        <div key={def.id} className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {def.fieldLabel}
                            {def.isRequired && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          {def.fieldType === 'text' && (
                            <input type="text" value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'number' && (
                            <input type="number" value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'date' && (
                            <input type="date" value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'textarea' && (
                            <textarea value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} rows={2} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500" />
                          )}
                          {def.fieldType === 'select' && (
                            <select value={flexVals[def.id] || ''} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-indigo-500 focus:border-indigo-500">
                              <option value="">-- Select --</option>
                              {(def.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          )}
                          {def.fieldType === 'checkbox' && (
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={(flexVals[def.id] || '') === 'true'} onChange={e => setFlexVals(prev => ({ ...prev, [def.id]: String(e.target.checked) }))} className="h-4 w-4 text-indigo-600 rounded" />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 sm:col-start-2"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Details View Modal */}
      {viewingContact && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setViewingContact(null)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
              {/* Close button */}
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={() => setViewingContact(null)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Contact Details
                </h3>

                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {viewingContact.firstName} {viewingContact.lastName}
                        {viewingContact.isVeteran && (
                          <span className="ml-2 inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800">
                            Veteran
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Email</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {viewingContact.emailAddress || '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Phone</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {viewingContact.phoneNumber || '-'}
                      </dd>
                    </div>
                  </div>

                  {viewingContact.mailingAddress && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Mailing Address</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {viewingContact.mailingAddress}
                      </dd>
                    </div>
                  )}

                  {/* Custom Fields */}
                  <FlexFieldValues entityType="contact" entityId={viewingContact.id} />

                  {/* Associated Plots */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        Associated Plots
                      </dt>
                    </div>

                    {/* Add Plot Buttons */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">Add as:</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => { setRelationshipType('owner'); setIsPlotModalOpen(true); }}
                          className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 ring-1 ring-inset ring-blue-200"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Owner
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRelationshipType('co-owner'); setIsPlotModalOpen(true); }}
                          className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 ring-1 ring-inset ring-indigo-200"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Co-owner
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRelationshipType('interred'); setIsPlotModalOpen(true); }}
                          className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100 ring-1 ring-inset ring-purple-200"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Interred
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRelationshipType('family'); setIsPlotModalOpen(true); }}
                          className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 ring-1 ring-inset ring-green-200"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Family
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRelationshipType('other'); setIsPlotModalOpen(true); }}
                          className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 ring-1 ring-inset ring-gray-200"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Other
                        </button>
                      </div>
                    </div>

                    <dd>
                      {contactPlots.length > 0 ? (
                        <div className="border rounded-md divide-y divide-gray-200">
                          {contactPlots.map((plotContact) => (
                            <div key={plotContact.id} className="flex items-center justify-between px-4 py-3">
                              <div>
                                <span className="font-medium text-gray-900">
                                  {plotContact.PlotPoly?.alias || `Plot ${plotContact.plotId}`}
                                </span>
                                <span className="ml-2 text-sm text-gray-500">
                                  ({plotContact.relationshipType})
                                </span>
                                {plotContact.isPrimary && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                    Primary
                                  </span>
                                )}
                                {plotContact.PlotPoly?.section && (
                                  <span className="ml-2 text-xs text-gray-400">
                                    Section: {plotContact.PlotPoly.section}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleViewPlotOnMap(plotContact.plotId)}
                                  className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900"
                                >
                                  <MapPinIcon className="h-4 w-4 mr-1" />
                                  View on Map
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePlotFromContact(plotContact.id)}
                                  className="inline-flex items-center text-sm text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No plots associated with this contact. Use the buttons above to add a plot.</p>
                      )}
                    </dd>
                  </div>

                  {/* Associated Sales */}
                  <div className="border-t pt-4">
                    <dt className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      Sales
                    </dt>
                    <dd>
                      {contactSales.length > 0 ? (
                        <div className="border rounded-md divide-y divide-gray-200">
                          {contactSales.map((sale) => (
                            <div key={sale.id} className="px-4 py-3">
                              <div className="flex items-center justify-between">
                                <div
                                  className="flex-1 cursor-pointer hover:text-indigo-600"
                                  onClick={() => navigate(`/sales?action=view&saleId=${sale.id}`)}
                                >
                                  <span className="font-semibold text-gray-900 hover:text-indigo-600">
                                    ${calculateTotal(sale.items).toFixed(2)}
                                  </span>
                                  <span className="ml-2 text-sm text-gray-500">
                                    {new Date(sale.createdAt).toLocaleDateString()}
                                  </span>
                                  {sale.status && (
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                      sale.status === 'paid' ? 'bg-green-100 text-green-800' :
                                      sale.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                                      sale.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {sale.status.replace('_', ' ')}
                                    </span>
                                  )}
                                </div>
                                {sale.status !== 'paid' && (
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/sales?action=payment&saleId=${sale.id}`)}
                                    className="ml-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 ring-1 ring-inset ring-green-200"
                                    title="Record Payment"
                                  >
                                    <CurrencyDollarIcon className="h-3 w-3 mr-1" />
                                    Pay
                                  </button>
                                )}
                              </div>
                              {sale.items && sale.items.length > 0 && (
                                <div
                                  className="text-xs text-gray-500 mt-1 cursor-pointer hover:text-indigo-600"
                                  onClick={() => navigate(`/sales?action=view&saleId=${sale.id}`)}
                                >
                                  {sale.items.slice(0, 3).map((item, idx) => (
                                    <span key={idx}>
                                      {idx > 0 && ', '}
                                      {item.name} x{item.qty}
                                    </span>
                                  ))}
                                  {sale.items.length > 3 && (
                                    <span className="text-gray-400"> +{sale.items.length - 3} more</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No sales associated with this contact</p>
                      )}
                    </dd>
                  </div>

                  {/* Associated Contacts */}
                  <div className="border-t pt-4">
                    <dt className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                      <UserGroupIcon className="h-4 w-4 mr-1" />
                      Related Contacts (via shared plots)
                    </dt>
                    <dd>
                      {associatedContacts.length > 0 ? (
                        <div className="border rounded-md divide-y divide-gray-200">
                          {associatedContacts.map((assoc, idx) => (
                            <div key={idx} className="px-4 py-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {assoc.contact.firstName} {assoc.contact.lastName}
                                  </span>
                                  {assoc.contact.emailAddress && (
                                    <span className="ml-2 text-sm text-gray-500">
                                      {assoc.contact.emailAddress}
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleViewContact(assoc.contact)}
                                  className="text-sm text-indigo-600 hover:text-indigo-900"
                                >
                                  View
                                </button>
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {assoc.sharedPlots.map((sp, spIdx) => (
                                  <span
                                    key={spIdx}
                                    className="inline-flex items-center text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                                  >
                                    {sp.plotAlias} ({sp.relationship})
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No related contacts found</p>
                      )}
                    </dd>
                  </div>

                  {/* Documents */}
                  <DocumentGenerationPanel
                    entityType="contact"
                    entityId={viewingContact.id}
                  />
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3 justify-end border-t pt-4">
                  <button
                    type="button"
                    onClick={() => handleCreateSaleForContact(viewingContact)}
                    className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                  >
                    <CurrencyDollarIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Create Sale
                  </button>
                  <button
                    type="button"
                    onClick={handleEditFromView}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    <PencilIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plot Search Modal for adding plots to contact */}
      <PlotSearchModal
        open={isPlotModalOpen}
        onClose={() => setIsPlotModalOpen(false)}
        onSelect={handleAddPlotToContact}
        excludeIds={contactPlots.map(pc => pc.plotId)}
      />
    </div>
  );
};

export default Contacts;
